import { formatExamDateOnly, formatExamTime } from '@exam-center/exam-event-management';
import { RegistrationType } from '@exam-center/graphql';
import { Flex, Grid, Text } from '@shared/components';
import { formatSecondsToHoursMinutes } from '@shared/utils';
import { Fragment, type ReactNode } from 'react';

import {
  RegistrationTypeLabel,
  type RegistrationType as RegistrationTypeForLabel,
} from '../registration-type-label';

export interface ExamRoundInformationProps {
  examCenterCode: string;
  examStartedAt: Date;
  examEndedAt: Date;
  examRoundNumber: number;
  examLocation: string;
  province: string;
  registrationType: RegistrationType;
  examSessions: {
    id: string;
    round: number;
    startedAt: Date;
    endedAt: Date;
  }[];
}

const RegistrationTypeMap: Record<RegistrationType, RegistrationTypeForLabel> = {
  [RegistrationType.InHouse]: 'IH',
  [RegistrationType.Public]: 'PUB',
};

export default function ExamRoundInformation(props: Readonly<ExamRoundInformationProps>) {
  const {
    examCenterCode,
    examStartedAt,
    examEndedAt,
    examRoundNumber,
    examLocation,
    province,
    registrationType,
    examSessions,
  } = props;

  const sessionInfo: {
    label: string;
    value: ReactNode;
    children?: ReactNode;
  }[] = [
    {
      label: 'ศูนย์ทดสอบ:',
      value: examCenterCode,
    },
    {
      label: 'รอบสอบที่:',
      value: examRoundNumber,
    },
    {
      label: 'วันที่สอบ:',
      value: formatExamDateOnly(examStartedAt),
    },
    {
      label: 'เวลาสอบ:',
      value: formatExamTime(examStartedAt, examEndedAt),
      children: examSessions.map((session) => (
        <Text key={session.id} variant='body2' color='textSecondary'>
          ช่วงที่ {session.round}: {formatExamTime(session.startedAt, session.endedAt)} ระยะเวลา:{' '}
          {formatSecondsToHoursMinutes(
            (session.endedAt.getTime() - session.startedAt.getTime()) / 1000,
            'TEXT',
          )}
        </Text>
      )),
    },
    {
      label: 'สถานที่สอบ:',
      value: (
        <Text variant='body1Semibold'>
          {examLocation}, {province}
        </Text>
      ),
    },
  ];

  return (
    <Flex
      sx={(theme) => ({
        p: theme.spacing(6),
        gap: theme.spacing(6),
        justifyContent: 'space-between',
        borderRadius: theme.shape.rounded(2),
        borderColor: theme.palette.divider,
        borderStyle: 'solid',
        borderWidth: '1px',
        backgroundColor: theme.palette.grey[50],
        width: '100%',
      })}
    >
      <Grid
        sx={(theme) => ({
          gridTemplateColumns: '120px 1fr',
          rowGap: theme.spacing(2),
          columnGap: theme.spacing(6),
        })}
      >
        {sessionInfo.map((info) => (
          <Fragment key={info.label}>
            <Text>{info.label}</Text>
            {typeof info.value === 'object' ? (
              info.value
            ) : (
              <Flex
                sx={(theme) => ({
                  flexDirection: 'column',
                  gap: theme.spacing(1),
                })}
              >
                <Text variant='body1Semibold'>{info.value}</Text>
                {info.children}
              </Flex>
            )}
          </Fragment>
        ))}
      </Grid>
      <RegistrationTypeLabel type={RegistrationTypeMap[registrationType]} variant='full' />
    </Flex>
  );
}
